import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from "react-icons/fa";
import { PlaySquare } from "lucide-react";
import { Heart } from "lucide-react";
import { api, formatCount } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePlayer } from "@/contexts/PlayerContext";
import { toast } from "sonner";

const ICONS = {
  YouTube: FaYoutube,
  TikTok: FaTiktok,
  Reels: FaInstagram,
  Facebook: FaFacebook,
  Shorts: PlaySquare,
};

export function SourceBadge({ source }) {
  const Icon = ICONS[source] || FaYoutube;
  return (
    <span className="inline-flex items-center gap-1 bg-black/80 backdrop-blur-md px-1.5 py-0.5 rounded text-[10px] font-medium text-white">
      <Icon size={10} /> {source}
    </span>
  );
}

export function VideoCard({ video, variant = "row", onUnsave }) {
  const { user, isSaved, markSaved } = useAuth();
  const { play } = usePlayer();
  const saved = isSaved(video.video_id);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please log in to save videos");
      return;
    }
    // optimistic
    const next = !saved;
    markSaved(video.video_id, next);
    try {
      if (next) {
        await api.post("/saved", { video });
        toast.success("Saved");
      } else {
        await api.delete(`/saved/${video.video_id}`);
        toast.success("Removed from saved");
        if (onUnsave) onUnsave(video.video_id);
      }
    } catch (err) {
      markSaved(video.video_id, !next); // revert
      toast.error("Could not update saved");
    }
  };

  const widthClass =
    variant === "row" ? "min-w-[240px] sm:min-w-[280px]" : "w-full";

  return (
    <button
      type="button"
      onClick={() => play(video)}
      data-testid={`video-card-${video.video_id}`}
      className={`group text-left flex flex-col gap-2 snap-start ${widthClass} animate-fade-up`}
    >
      <div className="aspect-video rounded-xl overflow-hidden bg-bg-surface relative">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-2 left-2 w-2 h-2 rounded-full bg-brand shadow-[0_0_8px_rgba(230,59,46,0.8)]" />
        <span className="absolute bottom-2 left-2">
          <SourceBadge source={video.source} />
        </span>
        <span className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-md px-1.5 py-0.5 rounded text-[10px] font-medium">
          {video.duration}
        </span>
        <button
          type="button"
          onClick={toggleSave}
          data-testid={`save-btn-${video.video_id}`}
          className={`absolute top-2 right-2 w-8 h-8 rounded-full grid place-items-center bg-black/60 backdrop-blur-md border border-white/10 transition ${
            saved ? "text-brand" : "text-white/85 hover:text-brand"
          }`}
          aria-label="Save video"
        >
          <Heart size={14} fill={saved ? "#e63b2e" : "transparent"} />
        </button>
      </div>
      <h3 className="font-heading text-sm sm:text-base font-medium leading-snug line-clamp-2 text-white/95">
        {video.title}
      </h3>
      <div className="flex items-center justify-between text-xs text-white/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-6 h-6 rounded-full bg-gradient-to-br from-brand to-orange-500 grid place-items-center text-[10px] font-bold text-white shrink-0">
            {video.channel?.charAt(0)?.toUpperCase() || "B"}
          </span>
          <span className="truncate">{video.channel}</span>
        </div>
        <span className="shrink-0 ml-2">{formatCount(video.view_count)}</span>
      </div>
    </button>
  );
}
